import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { TeamMember, ActiveFile } from '../types';
import { ArrowRightIcon, AlertCircleIcon, CheckCircle2Icon, SearchIcon } from 'lucide-react';
interface ReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  members: TeamMember[];
  onReassign: (sourceId: string, destId: string, fileIds: string[]) => void;
  initialSourceId?: string;
}
function getFileTypeBadgeVariant(fileType: string): 'blue' | 'purple' | 'success' | 'warning' | 'error' | 'default' | 'gray' {
  const map: Record<string, 'blue' | 'purple' | 'success' | 'warning' | 'error' | 'default' | 'gray'> = {
    Conventional: 'blue',
    FHA: 'purple',
    VA: 'success',
    Jumbo: 'warning',
    HELOC: 'default'
  };
  return map[fileType] || 'gray';
}
function getStatusBadge(status: ActiveFile['status']) {
  const config: Record<ActiveFile['status'], {
    variant: 'success' | 'blue' | 'warning' | 'purple' | 'default';
    label: string;
  }> = {
    'In Progress': {
      variant: 'blue',
      label: 'In Progress'
    },
    'Pending Docs': {
      variant: 'warning',
      label: 'Pending Docs'
    },
    'Under Review': {
      variant: 'purple',
      label: 'Under Review'
    },
    'Clear to Close': {
      variant: 'success',
      label: 'CTC'
    },
    Funded: {
      variant: 'success',
      label: 'Funded'
    }
  };
  const c = config[status];
  return <Badge variant={c.variant} data-id="element-114">{c.label}</Badge>;
}
export function ReassignModal({
  isOpen,
  onClose,
  members,
  onReassign,
  initialSourceId
}: ReassignModalProps) {
  const [sourceId, setSourceId] = useState<string>('');
  const [destId, setDestId] = useState<string>('');
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('All');
  const [step, setStep] = useState<'select' | 'confirm' | 'success'>('select');
  useEffect(() => {
    if (isOpen) {
      setSourceId(initialSourceId || '');
      setDestId('');
      setSelectedFileIds(new Set());
      setFileTypeFilter('All');
      setStep('select');
    }
  }, [isOpen, initialSourceId]);
  const sourceMember = members.find(m => m.id === sourceId);
  const destMember = members.find(m => m.id === destId);
  const filteredFiles = useMemo(() => {
    if (!sourceMember) return [];
    if (fileTypeFilter === 'All') return sourceMember.activeFiles;
    return sourceMember.activeFiles.filter(f => f.fileType === fileTypeFilter);
  }, [sourceMember, fileTypeFilter]);
  const allFileTypes = useMemo(() => {
    return Array.from(new Set(members.flatMap(m => m.activeFiles.map(f => f.fileType))));
  }, [members]);
  const toggleFile = (fileId: string) => {
    setSelectedFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };
  const toggleAll = () => {
    if (filteredFiles.every(f => selectedFileIds.has(f.id))) {
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        filteredFiles.forEach(f => next.delete(f.id));
        return next;
      });
    } else {
      setSelectedFileIds(prev => {
        const next = new Set(prev);
        filteredFiles.forEach(f => next.add(f.id));
        return next;
      });
    }
  };
  const selectedFiles = sourceMember ? sourceMember.activeFiles.filter(f => selectedFileIds.has(f.id)) : [];
  const destRemainingCapacity = destMember ? destMember.workload.capacity - destMember.workload.activeFiles : 0;
  const isOverCapacity = destMember ? selectedFiles.length > destRemainingCapacity : false;
  const handleConfirm = () => {
    if (sourceId && destId && selectedFiles.length > 0) {
      onReassign(sourceId, destId, Array.from(selectedFileIds));
      setStep('success');
    }
  };
  const handleClose = () => {
    onClose();
  };
  return <Modal isOpen={isOpen} onClose={handleClose} title="Mass Reassign Files" maxWidth="xl" data-id="element-115">
      {step === 'success' ? <div className="text-center py-8" data-id="element-116">
          <div className="mx-auto flex items-center justify-center h-14 w-14 rounded-full bg-green-100 mb-4" data-id="element-117">
            <CheckCircle2Icon className="h-8 w-8 text-green-600" data-id="element-118" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-119">
            Reassignment Complete
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-120">
            {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}{' '}
            moved from{' '}
            <span className="font-medium text-gray-700" data-id="element-121">
              {sourceMember?.name}
            </span>{' '}
            to{' '}
            <span className="font-medium text-gray-700" data-id="element-122">
              {destMember?.name}
            </span>
            .
          </p>
          <Button onClick={handleClose} data-id="element-123">Done</Button>
        </div> : step === 'confirm' ? <div className="space-y-5" data-id="element-124">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-125">
            <AlertCircleIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-126" />
            <div className="text-sm text-amber-800" data-id="element-127">
              <p className="font-medium mb-1" data-id="element-128">Confirm Reassignment</p>
              <p data-id="element-129">
                You are about to move{' '}
                <strong data-id="element-130">
                  {selectedFiles.length} file
                  {selectedFiles.length !== 1 ? 's' : ''}
                </strong>{' '}
                from <strong data-id="element-131">{sourceMember?.name}</strong> to{' '}
                <strong data-id="element-132">{destMember?.name}</strong>. This will immediately
                update Salesforce records.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4" data-id="element-133">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-134">
              Files to Reassign
            </h4>
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-id="element-135">
              {selectedFiles.map(file => <div key={file.id} className="flex items-center justify-between text-xs bg-white rounded px-3 py-2 border border-gray-200" data-id="element-136">
                  <div className="flex items-center space-x-3" data-id="element-137">
                    <span className="font-mono font-medium text-blue-600" data-id="element-138">
                      {file.loanNumber}
                    </span>
                    <span className="text-gray-700" data-id="element-139">{file.borrowerName}</span>
                  </div>
                  <Badge variant={getFileTypeBadgeVariant(file.fileType)} data-id="element-140">
                    {file.fileType}
                  </Badge>
                </div>)}
            </div>
          </div>

          {isOverCapacity && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-141">
              ⚠️ This will exceed {destMember?.name}'s capacity (
              {destRemainingCapacity} open slots for {selectedFiles.length}{' '}
              files).
            </div>}

          <div className="flex justify-between pt-2 border-t border-gray-100" data-id="element-142">
            <Button variant="outline" onClick={() => setStep('select')} data-id="element-143">
              Back
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-144">
              Confirm Reassignment ({selectedFiles.length} files)
            </Button>
          </div>
        </div> : <div className="space-y-5" data-id="element-145">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start space-x-3" data-id="element-146">
            <AlertCircleIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" data-id="element-147" />
            <p className="text-xs text-blue-800" data-id="element-148">
              Select a source agent, pick the files to move, then choose a
              destination agent.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start" data-id="element-149">
            <div className="space-y-1.5" data-id="element-150">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-151">
                From Agent
              </label>
              <select value={sourceId} onChange={e => {
            setSourceId(e.target.value);
            setSelectedFileIds(new Set());
            setFileTypeFilter('All');
          }} className="w-full rounded-lg border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" data-id="element-152">
                <option value="" data-id="element-153">Select source...</option>
                {members.map(m => <option key={m.id} value={m.id} disabled={m.activeFiles.length === 0} data-id="element-154">
                    {m.name} ({m.activeFiles.length} files)
                  </option>)}
              </select>
              {sourceMember && <p className="text-[10px] text-gray-500" data-id="element-155">
                  {sourceMember.activeFiles.length} active files ·{' '}
                  {sourceMember.title}
                </p>}
            </div>

            <div className="hidden md:flex justify-center pt-7" data-id="element-156">
              <div className="bg-gray-100 rounded-full p-2" data-id="element-157">
                <ArrowRightIcon className="h-4 w-4 text-gray-400" data-id="element-158" />
              </div>
            </div>

            <div className="space-y-1.5" data-id="element-159">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-160">
                To Agent
              </label>
              <select value={destId} onChange={e => setDestId(e.target.value)} className="w-full rounded-lg border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" data-id="element-161">
                <option value="" data-id="element-162">Select destination...</option>
                {members.filter(m => m.id !== sourceId).map(m => <option key={m.id} value={m.id} disabled={!m.isActive} data-id="element-163">
                      {m.name} ({m.workload.capacity - m.workload.activeFiles}{' '}
                      open)
                    </option>)}
              </select>
              {destMember && <p className="text-[10px] text-gray-500" data-id="element-164">
                  {destMember.workload.capacity - destMember.workload.activeFiles}{' '}
                  open slots · {destMember.title}
                </p>}
            </div>
          </div>

          {sourceMember && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-165">
              <div className="bg-gray-50 px-4 py-2.5 flex items-center justify-between border-b border-gray-200" data-id="element-166">
                <div className="flex items-center space-x-3" data-id="element-167">
                  <h4 className="text-xs font-semibold text-gray-700" data-id="element-168">
                    Select Files ({selectedFileIds.size} selected)
                  </h4>
                  <select value={fileTypeFilter} onChange={e => setFileTypeFilter(e.target.value)} className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-169">
                    <option value="All" data-id="element-170">All Types</option>
                    {allFileTypes.map(type => <option key={type} value={type} data-id="element-171">
                        {type}
                      </option>)}
                  </select>
                </div>
                <button onClick={toggleAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-172">
                  {filteredFiles.every(f => selectedFileIds.has(f.id)) ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-100" data-id="element-173">
                {filteredFiles.length === 0 ? <div className="text-center py-6 text-gray-400 text-xs" data-id="element-174">
                    No files match the selected filter.
                  </div> : filteredFiles.map(file => <label key={file.id} className={`flex items-center px-4 py-2.5 cursor-pointer hover:bg-blue-50/50 transition-colors ${selectedFileIds.has(file.id) ? 'bg-blue-50/30' : ''}`} data-id="element-175">
                      <input type="checkbox" checked={selectedFileIds.has(file.id)} onChange={() => toggleFile(file.id)} className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3 flex-shrink-0" data-id="element-176" />
                      <div className="flex items-center justify-between w-full min-w-0" data-id="element-177">
                        <div className="flex items-center space-x-3 min-w-0" data-id="element-178">
                          <span className="font-mono text-xs font-medium text-blue-600 flex-shrink-0" data-id="element-179">
                            {file.loanNumber}
                          </span>
                          <span className="text-xs text-gray-700 truncate" data-id="element-180">
                            {file.borrowerName}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0 ml-3" data-id="element-181">
                          {getStatusBadge(file.status)}
                          <Badge variant={getFileTypeBadgeVariant(file.fileType)} data-id="element-182">
                            {file.fileType}
                          </Badge>
                        </div>
                      </div>
                    </label>)}
              </div>
            </div>}

          <div className="flex justify-end space-x-3 pt-2 border-t border-gray-100" data-id="element-183">
            <Button variant="outline" onClick={handleClose} data-id="element-184">
              Cancel
            </Button>
            <Button variant="primary" disabled={!sourceId || !destId || selectedFileIds.size === 0 || sourceId === destId} onClick={() => setStep('confirm')} data-id="element-185">
              Review Reassignment ({selectedFileIds.size})
            </Button>
          </div>
        </div>}
    </Modal>;
}